import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { useQuery } from '@connectrpc/connect-query'
import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { type AreaCodeDemandData as ProtoAreaCodeDemandData } from '@ebalance/proto/bffuser/v1/balance_model_pb'
import { getDemandDashboard } from '@ebalance/proto/bffuser/v1/balance_service-BalanceDemandService_connectquery'
import {
  GetDemandDashboardRequestSchema,
  type GetDemandDashboardResponse,
} from '@ebalance/proto/bffuser/v1/balance_service_pb'
import type { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { keepPreviousData } from '@tanstack/react-query'

import { isRegionLoading } from '~/hooks/isRegionLoading'
import { toProtoDate } from '~/models/dateTime'
import { timeToKomaNumber } from '~/models/komaToTime'
import type { KomaData } from '~/pages/BalanceDemand/models/generateMockData'

/**
 * カードの MAX/MIN ピルバッジが描く 需要ピーク (backend 算出、doji-bg.md
 * AC-BDB-002)。詳細画面の DetailSummary と同じ形 (proto は DemandPeakSummary を
 * 共用)。実績が 1 コマも無いエリア、または実績値のあるコマがすべて低圧未着の
 * エリア (低圧未着のコマはピーク候補から外す) では null で、カードはピル行ごと描かない。
 */
export type AreaCodePeakSummary = {
  maxActual: number
  maxTime: string
  maxKoma: number
  minActual: number
  minTime: string
  minKoma: number
}

export type AreaCodeApiData = {
  areaCode: AreaCode
  baseScale: number
  baseTemp: number
  komaData: KomaData[]
  summary: AreaCodePeakSummary | null
}

function convertKoma(k: {
  time: string
  demandPlanValue?: number
  demandActual?: number
  demandActualCorrected?: number
  temperature?: number
  temperatureForecast?: number
  dewPoint?: number
  dewPointForecast?: number
  trendPlan?: number
  procurementNet?: number
  demandExtraHigh?: number
  demandHigh?: number
  demandLow?: number
  precipitation?: number
  awaitingVoltageClasses: VoltageClass[]
}): KomaData {
  return {
    // dashboard の応答はコマ番号列を持たないので、開始時刻から復元する
    // (詳細 RPC の `koma` と同じ 1 始まり)。配列 index を使わないのは、
    // 応答が時刻順である保証を frontend が握っていないため。
    koma: timeToKomaNumber(k.time),
    time: k.time,
    demandPlan: k.demandPlanValue ?? null,
    demandActual: k.demandActual ?? null,
    demandActualCorrected: k.demandActualCorrected ?? null,
    temperature: k.temperature ?? null,
    temperatureForecast: k.temperatureForecast ?? null,
    // 露点は #2002 まで backend が nil を返す。0 に潰すと欠測が「実在する
    // 0℃」として描かれる (詳細側 converter と同じく null で通す)。
    dewPoint: k.dewPoint ?? null,
    trendPlan: k.trendPlan ?? null,
    procurementNet: k.procurementNet ?? null,
    demandExtraHigh: k.demandExtraHigh ?? null,
    demandHigh: k.demandHigh ?? null,
    demandLow: k.demandLow ?? null,
    precipitation: k.precipitation ?? null,
    awaitingVoltageClasses: k.awaitingVoltageClasses,
  }
}

function convertAreaCodeData(r: ProtoAreaCodeDemandData): AreaCodeApiData {
  return {
    areaCode: r.areaCode,
    baseScale: r.baseScale,
    baseTemp: r.baseTemp,
    komaData: r.komas.map(convertKoma),
    // proto の absent は「そのエリアに実績値が 1 コマも無い」(未来日 / 取込前)、
    // または「実績値のあるコマがすべて低圧未着」。
    // 0 埋めするとカードが「MAX 0 kWh / 00:00」を実測のように出すので null で通す。
    summary: r.summary ?? null,
  }
}

export function convertDashboardResponse(resp: GetDemandDashboardResponse): {
  areaCodes: AreaCodeApiData[]
  gcKoma: number
  currentKoma: number
  /** 託送実績データの最終取込完了時刻。undefined = 未取込 (AC-BDB-002-1)。 */
  takusoLastUpdatedAt?: Timestamp
} {
  return {
    areaCodes: resp.areaCodes.map(convertAreaCodeData),
    gcKoma: resp.gcKoma,
    currentKoma: resp.currentKoma,
    takusoLastUpdatedAt: resp.takusoLastUpdatedAt,
  }
}

export function useDemandDashboard(displayDate: Date): {
  data: GetDemandDashboardResponse | undefined
  isError: boolean
  /** 領域 (エリアカード群) を影 + スピナーで覆うか。 判断は `~/hooks/isRegionLoading`。 */
  isRegionLoading: boolean
} {
  const query = useQuery(
    getDemandDashboard,
    create(GetDemandDashboardRequestSchema, {
      displayDate: toProtoDate(displayDate),
    }),
    {
      refetchInterval: 5_000,
      // 表示日を変えた直後も前の日のカードを残し、 影 + スピナーで覆う
      // (UI 辞書 components/region-loading.md「フィルタの変更で前の内容を消さない」)。
      // 覆うのは key が変わった後の取得 (`isPlaceholderData`) だけで、 5 秒ごとの
      // 自動更新では覆わない。
      placeholderData: keepPreviousData,
      // `ApiClientProvider` の既定は `throwOnError: true` (= 取得失敗を render 中に
      // throw して router の errorElement = 全画面エラーへ飛ばす)。 この画面は
      // spec (AC-BDB-013 / AC-BDH-013) が**画面内のエラーバナー**を要求するので、
      // この query だけ false に上書きして `isError` を購読側で扱う
      // (`WorkflowBackBar` と同じ形。 throwOnError は observer ごとに評価される)。
      throwOnError: false,
    },
  )
  return {
    data: query.data,
    isError: query.isError,
    isRegionLoading: isRegionLoading(query),
  }
}
